import { Clock as ThreeClock } from 'three';
import { EventEmitter } from 'events';

export type MeasureTypes = '4/4' | '3/4' | '6/8' | '7/8' | '9/8' | '12/8';

export interface BeatInfo {
    beat: number;
    measure: number;
    progress: number;  // Progress within the current beat (0-1)
    anticipation: number;  // Anticipation factor (0-1), reaches 1 just before the next beat
}

export class Clock extends ThreeClock {
    private _bpm: number;
    private _measure: MeasureTypes;
    private _eventEmitter: EventEmitter;
    private _lastBeat: number = 0;
    private _lastMeasure: number = 1;
    private _anticipationThreshold: number = 0.8;  // When to start anticipating the next beat (0-1)
    private _lastTickTime: number = 0;
    private _beatDuration: number = 0;  // Duration of one beat in seconds
    private _performanceMode: boolean = false;  // High-performance mode for smoother animations

    constructor(bpm: number = 120, measure: MeasureTypes = '4/4', autoStart: boolean = true) {
        super(autoStart);
        this._bpm = this.validateBpm(bpm);
        this._measure = measure;
        this._eventEmitter = new EventEmitter();
        this._beatDuration = 60 / this._bpm;
        this._lastTickTime = performance.now();
    }

    private validateBpm(bpm: number): number {
        if (bpm < 20) return 20;
        if (bpm > 300) return 300;
        return bpm;
    }

    setBpm(bpm: number): void {
        this._bpm = this.validateBpm(bpm);
        this._beatDuration = 60 / this._bpm;
    }

    getBpm(): number {
        return this._bpm;
    }

    getMeasure(): MeasureTypes {
        return this._measure;
    }

    setMeasure(measure: MeasureTypes): void {
        this._measure = measure;
    }

    reset(): void {
        this.stop();
        this.elapsedTime = 0;
        this._lastBeat = 0;
        this._lastMeasure = 1;
        this._lastTickTime = performance.now();
    }

    get measure(): MeasureTypes {
        return this._measure;
    }

    get bpm(): number {
        return this._bpm;
    }

    /**
     * Enable high-performance mode for smoother animations
     * This uses more CPU but provides more accurate timing
     */
    setPerformanceMode(enabled: boolean): void {
        this._performanceMode = enabled;
    }

    /**
     * Set the anticipation threshold for beat detection
     * @param threshold Value between 0-1, where 1 means no anticipation
     * and 0 means always anticipating the next beat
     */
    setAnticipationThreshold(threshold: number): void {
        this._anticipationThreshold = Math.max(0, Math.min(1, threshold));
    }

    /**
     * Get the current beat (1-based)
     */
    getCurrentBeat(): number {
        const beatsPerSecond = this._bpm / 60;
        const totalBeats = Math.floor(this.elapsedTime * beatsPerSecond);
        const beatsPerMeasure = parseInt(this._measure.split('/')[0]);
        return (totalBeats % beatsPerMeasure) + 1;
    }

    /**
     * Get the current measure (1-based)
     */
    getCurrentMeasure(): number {
        const beatsPerSecond = this._bpm / 60;
        const totalBeats = Math.floor(this.elapsedTime * beatsPerSecond);
        const beatsPerMeasure = parseInt(this._measure.split('/')[0]);
        return Math.floor(totalBeats / beatsPerMeasure) + 1;
    }

    /**
     * Get detailed information about the current beat
     * Includes progress within the beat and anticipation factor
     */
    getBeatInfo(): BeatInfo {
        const beatsPerSecond = this._bpm / 60;
        const totalBeats = this.elapsedTime * beatsPerSecond;
        const beatsPerMeasure = parseInt(this._measure.split('/')[0]);
        
        const currentBeat = (Math.floor(totalBeats) % beatsPerMeasure) + 1;
        const currentMeasure = Math.floor(totalBeats / beatsPerMeasure) + 1;
        
        // Calculate progress within the current beat (0-1)
        const progress = totalBeats % 1;
        
        // Calculate anticipation factor (0-1)
        // This reaches 1 just before the next beat
        const anticipation = progress >= this._anticipationThreshold 
            ? (progress - this._anticipationThreshold) / (1 - this._anticipationThreshold)
            : 0;
        
        return {
            beat: currentBeat,
            measure: currentMeasure,
            progress,
            anticipation
        };
    }

    addEventListener(event: string, listener: (...args: any[]) => void): void {
        this._eventEmitter.on(event, listener);
    }

    removeEventListener(event: string, listener: (...args: any[]) => void): void {
        this._eventEmitter.off(event, listener);
    }

    /**
     * Get elapsed time with improved accuracy and beat detection
     */
    getElapsedTime(): number {
        // In high-performance mode, use performance.now() for more accurate timing
        if (this._performanceMode && this.running) {
            const now = performance.now();
            const deltaTime = (now - this._lastTickTime) / 1000;
            this._lastTickTime = now;
            
            // Update the elapsed time manually for better accuracy
            this.elapsedTime += deltaTime;
        } else {
            // Use Three.js clock in normal mode
            super.getElapsedTime();
        }
        
        // Check for beat changes
        const beatInfo = this.getBeatInfo();
        
        if (beatInfo.beat !== this._lastBeat || beatInfo.measure !== this._lastMeasure) {
            this._lastBeat = beatInfo.beat;
            this._lastMeasure = beatInfo.measure;
            this._eventEmitter.emit('beat', beatInfo);
        }
        
        // Emit a continuous tick event for animations
        this._eventEmitter.emit('tick', beatInfo);
        
        return this.elapsedTime;
    }
}